// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import { oneOfType, object, func, string, bool, array } from 'prop-types';
import noop from 'lodash/noop';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';

import AddToWishlistMenuComponent from 'Components/Wishlists/AddToWishlistMenu';
import ProductPropType from 'Types/product';
import useModals from 'Hooks/useModals';
import useAnalytics from 'Hooks/useAnalytics';
import useFetch from 'Hooks/useFetch';
import useOffers from 'Hooks/useOffers';
import useToastNotifications from 'Hooks/useToastNotifications';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import { WishlistContext } from 'Contexts/WishlistContext';
import isOfferExpired, {
  clampMarkupPercent,
  getOfferProductErrors,
  isProductUnavailableForOffer,
  OFFER_PRODUCT_STATUS_TRANSLATIONS
} from 'Utils/offers';
import { API_OFFERS, PERMISSION } from 'Constants';

const getProductId = product => product?.productId || product?.id;

const getItemPayload = (product, defaultMarkupPercent) => ({
  productId: String(getProductId(product)),
  quantity: Number(product.quantity || product.minQuantity || 1),
  ...(product.supplierId !== null
    && product.supplierId !== undefined
    && { supplierId: product.supplierId }),
  markupPercent: clampMarkupPercent(
    product.markupPercent ?? defaultMarkupPercent ?? 0
  ),
  unitCustomPrice: product.unitCustomPrice ?? null
});

const AddToWishlistMenu = (
  {
    onAddToWishlist = noop,
    product,
    wishlist,
    loading,
    ...rest
  }
) => {
  const { t } = useTranslation();
  const modalContext = useModals();
  const fetcher = useFetch();
  const wishlists = useContextSelector(WishlistContext, data => data.data);
  const analytics = useAnalytics();
  const toast = useToastNotifications();
  const { data: { isImpersonated } = {} } = useUser();
  const { can } = usePermissions();
  const canAccessOffers = !isImpersonated && can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const {
    offers,
    activeOffer,
    activeOfferId,
    activeOfferStorage,
    currentDestination,
    addProductToOffer
  } = useOffers();
  const [checkingSavedOffers, setCheckingSavedOffers] = useState(false);
  const availableOffers = offers.filter(offer => !isOfferExpired(offer));

  const handleOnOpenModal = (mode = 'select') => {
    modalContext.setState(modalContext.modalState.ADD_TO_WISHLIST.key, {
      product,
      wishlist,
      action: rest?.action || '',
      mode,
      open: true
    });
  };

  const handleOnAddToWishlist = async selectedWishlistId => {
    if (wishlists) {
      const defaultWishlist = selectedWishlistId
        ? Object.values(wishlists).find(list => list.id === selectedWishlistId)
        : Object.values(wishlists).find(list => list.isDefault);
      analytics.track('wishlist_add_product_to_default_wishlist', {
        product_id: product?.id || '',
        product_sku: product?.code || '',
        wishlist_id: defaultWishlist?.id || ''
      });
      return defaultWishlist
        ? onAddToWishlist({ selectedItem: defaultWishlist })
        : handleOnOpenModal('select');
    }

    return null;
  };

  const handleAddToSavedOffer = async offerId => {
    if (isProductUnavailableForOffer(product)) {
      toast.addNotification({
        type: 'error',
        title: t('offers.productUnavailable')
      });
      return null;
    }

    setCheckingSavedOffers(true);
    try {
      const offer = await fetcher(`${ API_OFFERS }/${ encodeURIComponent(offerId) }`);
      if (!offer || isOfferExpired(offer)) {
        return handleOnOpenModal('select');
      }

      const productId = getProductId(product);
      const alreadyExists = (offer.items || []).some(
        item => String(getProductId(item)) === String(productId)
      );
      if (!alreadyExists) {
        await fetcher(`${ API_OFFERS }/${ encodeURIComponent(offerId) }/items`, {
          method: 'POST',
          body: JSON.stringify(getItemPayload(product, offer.defaultMarkupPercent))
        });
      }

      analytics.track('offer_add_product_to_active_offer', {
        offer_id: offerId,
        storage: 'saved',
        product_id: productId || '',
        product_sku: product?.code || '',
        result: alreadyExists ? 'already_exists' : 'added'
      });
      toast.addNotification({
        type: alreadyExists ? 'info' : 'success',
        title: alreadyExists
          ? t('offers.productAlreadyExists')
          : t('offers.productAdded', { name: offer.name })
      });
      return { offer, alreadyExists };
    } catch (error) {
      const productErrors = getOfferProductErrors(error);
      toast.addNotification({
        type: 'error',
        title: productErrors.length
          ? t(
            OFFER_PRODUCT_STATUS_TRANSLATIONS[productErrors[0].status]
            || 'offers.cartStatusProductUnavailable'
          )
          : error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.productAddFailed')
      });
      return null;
    } finally {
      setCheckingSavedOffers(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!canAccessOffers) {
      return handleOnAddToWishlist();
    }
    if (rest?.action === 'move') {
      return handleOnOpenModal('select');
    }

    if (currentDestination?.type === 'wishlist') {
      return handleOnAddToWishlist(currentDestination.id);
    }

    if (activeOfferStorage === 'saved' && activeOfferId) {
      return handleAddToSavedOffer(activeOfferId);
    }

    if (!availableOffers.length) {
      setCheckingSavedOffers(true);
      try {
        const response = await fetcher(API_OFFERS);
        const hasAvailableSavedOffers = response.items
          .some(offer => !isOfferExpired(offer));
        return handleOnOpenModal(hasAvailableSavedOffers ? 'select' : 'createOffer');
      } catch (error) {
        return handleOnOpenModal('select');
      } finally {
        setCheckingSavedOffers(false);
      }
    }

    if (!activeOffer || isOfferExpired(activeOffer)) {
      return handleOnOpenModal('select');
    }

    const result = addProductToOffer(activeOffer.id, product, { allowOutOfStock: true });
    if (result?.unavailable) {
      toast.addNotification({
        type: 'error',
        title: t('offers.productUnavailable')
      });
      return result;
    }
    if (!result || result.expired) {
      return handleOnOpenModal(availableOffers.length ? 'select' : 'createOffer');
    }
    analytics.track('offer_add_product_to_active_offer', {
      offer_id: activeOffer.id,
      product_id: product?.id || '',
      product_sku: product?.code || ''
    });
    toast.addNotification({
      type: result?.alreadyExists ? 'info' : 'success',
      title: result?.alreadyExists
        ? t('offers.productAlreadyExists')
        : t('offers.productAdded', { name: activeOffer.name })
    });
    return result;
  };

  const destinationIsWishlist = !canAccessOffers || currentDestination?.type === 'wishlist';

  return (
    <AddToWishlistMenuComponent
      onOpenModal={ () => handleOnOpenModal('select') }
      selectedProduct={ product }
      onAddToWishlist={ handlePrimaryAction }
      loading={ loading || checkingSavedOffers }
      label={
        rest.label
        || t(destinationIsWishlist ? 'offers.addToList' : 'offers.addToOffer')
      }
      offerFirst={ !destinationIsWishlist && rest?.action !== 'move' }
      { ...rest }
    />
  );
};

AddToWishlistMenu.propTypes = {
  onAddToWishlist: func,
  action: string,
  items: array,
  wishlist: object,
  loading: bool,
  hasDefault: bool,
  canCreate: bool,
  product: oneOfType([object, ProductPropType]),
  label: string
};

export default AddToWishlistMenu;
